import { useCallback, useEffect, useMemo, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { useTranslate } from "@similarweb/i18n-provider";

import { useApiQuery } from "app/modules/api-client";

import type { ContentFilterDraft } from "../types";

import { useContentFiltersMutation } from "./useContentFiltersMutation";
import { DEFAULT_FILTER_TARGET, draftsToFilters, filtersToDrafts, validateFilters } from "./validation";

export type UseContentFilterDraftsParams = {
    activationId: string;
    allDomains: string[];
    setIsChanged: (isChanged: boolean) => void;
};

const createEmptyDraft = (domain = ""): ContentFilterDraft => ({
    id: uuidv4(),
    domain,
    filterTarget: DEFAULT_FILTER_TARGET,
    positiveKeywords: "",
    negativeKeywords: "",
});

export const useContentFilterDrafts = ({ activationId, allDomains, setIsChanged }: UseContentFilterDraftsParams) => {
    const { translate } = useTranslate();
    const {
        data: serverFilters,
        isLoading,
        isError,
    } = useApiQuery(
        {
            tag: "Alerts",
            methodName: "userdataActivationAlertsContentFiltersDetail",
        },
        activationId,
    );
    const { mutateAsync: saveContentFilters } = useContentFiltersMutation({
        hasExistingData: !!serverFilters?.length,
        activationId,
    });

    const [drafts, setDrafts] = useState<ContentFilterDraft[]>([]);
    const [hasValidated, setHasValidated] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (serverFilters) {
            setDrafts(filtersToDrafts(serverFilters, uuidv4));
        }
    }, [serverFilters]);

    const domainsSet = useMemo(() => new Set(allDomains), [allDomains]);

    // removed domain cleanup in drafts
    useEffect(() => {
        const shouldCleanup = drafts.some((draft) => draft.domain && !domainsSet.has(draft.domain));

        if (shouldCleanup) {
            setDrafts((prev) =>
                prev.map((draft) => (draft.domain && !domainsSet.has(draft.domain) ? { ...draft, domain: "" } : draft)),
            );
            setIsDirty(true);
        }
    }, [domainsSet, drafts]);

    const validation = useMemo(() => validateFilters({ drafts, translate }), [drafts, translate]);

    const selectedDomains = useMemo(() => new Set(drafts.map((draft) => draft.domain).filter(Boolean)), [drafts]);

    const handleChange = () => {
        setIsDirty(true);
        setIsChanged(true);
    };

    const handleChangeFilter = (id: string, patch: Partial<ContentFilterDraft>) => {
        setDrafts((prev) => prev.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));
        handleChange();
    };

    const handleDeleteFilter = (id: string) => {
        setDrafts((prev) => prev.filter((draft) => draft.id !== id));
        handleChange();
    };

    const handleAddFilter = () => {
        const nextDomain = allDomains.find((domain) => !selectedDomains.has(domain)) ?? "";

        setDrafts((prev) => [...prev, createEmptyDraft(nextDomain)]);
        handleChange();
    };

    const handleValidateFilters = useCallback(async (): Promise<void> => {
        if (!isDirty) {
            return Promise.resolve();
        }

        if (!validation.isValid) {
            setHasValidated(true);

            return Promise.reject(new Error("Filters validation failed"));
        }
    }, [isDirty, validation.isValid]);

    const handleSave = useCallback(async (): Promise<void> => {
        if (!isDirty) {
            return Promise.resolve();
        }

        await saveContentFilters(draftsToFilters(drafts));

        setHasValidated(false);
        setIsDirty(false);
    }, [drafts, isDirty, saveContentFilters]);

    return {
        drafts,
        handleSave,
        handleValidateFilters,
        isError,
        isLoading,
        onAddFilter: handleAddFilter,
        onChangeFilter: handleChangeFilter,
        onDeleteFilter: handleDeleteFilter,
        selectedDomains,
        validationErrors: hasValidated ? validation.errors : {},
    };
};
